import { motion } from "framer-motion";
import type { AiPipelineAccentStyle } from "./aiPipelineAccentStyles";

export type AiPipelineIndeterminateProgressProps = {
  accent: AiPipelineAccentStyle;
  reducedMotion?: boolean;
  estimatedProgressPercent?: number;
};

export const AiPipelineIndeterminateProgress = ({
  accent,
  reducedMotion = false,
  estimatedProgressPercent,
}: AiPipelineIndeterminateProgressProps) => {
  const hasEstimate = estimatedProgressPercent !== undefined;
  const fillPercent = hasEstimate ? Math.max(estimatedProgressPercent ?? 0, 6) : 0;

  return (
    <div className="flex w-full flex-col">
      <div
        className={`relative h-1.5 w-full overflow-hidden rounded-full ${accent.progressTrack}`}
        role="progressbar"
        aria-valuetext={
          hasEstimate ? `Estimated progress ${estimatedProgressPercent}%` : "In progress"
        }
        aria-valuenow={hasEstimate ? estimatedProgressPercent : undefined}
        aria-valuemin={hasEstimate ? 0 : undefined}
        aria-valuemax={hasEstimate ? 100 : undefined}
      >
        {hasEstimate ? (
          <div
            className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${accent.progressShimmer} opacity-80 transition-[width] duration-700 ease-out`}
            style={{ width: `${fillPercent}%` }}
          />
        ) : null}
        {reducedMotion ? (
          <div
            className={`absolute inset-y-0 w-[30%] rounded-full bg-gradient-to-r ${accent.progressShimmer} opacity-90`}
            style={{ left: hasEstimate ? `${Math.max(fillPercent - 15, 0)}%` : "35%" }}
          />
        ) : (
          <motion.div
            className={`absolute inset-y-0 w-[28%] rounded-full bg-gradient-to-r ${accent.progressShimmer}`}
            animate={{ left: hasEstimate ? ["0%", "72%"] : ["-30%", "110%"] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            style={
              hasEstimate ? { left: `${Math.min(Math.max(fillPercent - 20, 0), 72)}%` } : undefined
            }
          />
        )}
      </div>
    </div>
  );
};
